import { queryKeys, useHttpClient } from '@repo/api-client/react';
import {
  BusinessTypesResponseSchema,
  OrgDefaultsSchema,
  PatchOrgDefaultsRequestSchema,
  SupportedCurrenciesResponseSchema,
  type OrgDefaults,
  type PatchOrgDefaultsRequest,
} from '@repo/api-client/dtos';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getJSON, patchJSON } from '../internal/http';

/**
 * Per-org defaults bag. The server stores this as an arbitrary JSON
 * object so screens can stash invoice prefix, default tax rate, etc.
 */
export function useAppDefaults(organizationId: string) {
  const http = useHttpClient();
  const {
    error,
    isPending: loading,
    data: appDefaults,
  } = useQuery({
    queryKey: organizationId
      ? queryKeys.appDefaults.byOrg(organizationId)
      : ['app-defaults', 'idle'],
    queryFn: () =>
      getJSON(http, `/orgs/${organizationId}/defaults`, OrgDefaultsSchema),
    enabled: !!organizationId,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  return { appDefaults: appDefaults as OrgDefaults | undefined, loading, error };
}

export function useSupportedCurrencies() {
  const http = useHttpClient();
  const {
    error,
    isPending: loading,
    data,
  } = useQuery({
    queryKey: queryKeys.appDefaults.supportedCurrencies(),
    queryFn: () => getJSON(http, '/catalog/currencies', SupportedCurrenciesResponseSchema),
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
  });

  return { currencies: data?.items, loading, error };
}

export function useBusinessTypeTemplates() {
  const http = useHttpClient();
  const {
    error,
    isPending: loading,
    data,
  } = useQuery({
    queryKey: queryKeys.appDefaults.businessTypes(),
    queryFn: () => getJSON(http, '/catalog/business-types', BusinessTypesResponseSchema),
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
  });

  return { templates: data?.items, loading, error };
}

export function useUpdateAppDefaults() {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({
      organizationId,
      updates,
    }: {
      organizationId: string;
      updates: PatchOrgDefaultsRequest;
    }) => {
      // Validate the patch shape locally — the server is permissive
      // and accepts any string-keyed JSON, but we want misuse to fail
      // loud rather than silently round-trip a wrong field.
      PatchOrgDefaultsRequestSchema.parse(updates);
      return patchJSON(
        http,
        `/orgs/${organizationId}/defaults`,
        updates,
        OrgDefaultsSchema,
      );
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.appDefaults.byOrg(updated.orgId), updated);
      queryClient.invalidateQueries({ queryKey: ['app-defaults'] });
    },
  });

  return {
    updateDefaults: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
